import React from 'react';
import type {
    SearchSuggestion,
    SearchSuggestionGroup,
} from '../view-models/search-suggestion';
import { MixedSuggestionItem } from './MixedSuggestionItem';
import { useSearchTracking } from '../hooks/use-search-tracking';

interface SearchSuggestionMixProps {
    groups: SearchSuggestionGroup[];
    query: string;
    onOptionClick: (suggestion: SearchSuggestion | string) => void;
    variant?: 'default' | 'header';
    menuColor?: 'light' | 'dark';
}

interface MixedSuggestion {
    option: SearchSuggestion | string;
    groupType: 'keyword' | 'categories' | 'professionals';
}

/**
 * Component for displaying mixed suggestions from all groups
 * Shows max 10 items mixing keywords, categories, and professionals
 */
export const SearchSuggestionMix: React.FC<SearchSuggestionMixProps> = ({
    groups,
    query,
    onOptionClick,
    variant = 'default',
    menuColor = 'dark',
}) => {
    const trackingConfig = useSearchTracking();

    // Mix all options from all groups and limit to 10
    // Use a Set to track unique items and prevent duplicates
    const seenKeys = new Set<string>();
    const mixedItems: MixedSuggestion[] = [];

    groups.forEach((group) => {
        group.options.forEach((option) => {
            // Create a unique key for deduplication
            const uniqueKey =
                typeof option === 'string'
                    ? `keyword:${option}`
                    : `${group.type}:${option.link}:${option.title}`;

            // Only add if we haven't seen this item before
            if (!seenKeys.has(uniqueKey)) {
                seenKeys.add(uniqueKey);
                mixedItems.push({
                    option,
                    groupType: group.type,
                });
            }
        });
    });

    // Sort items by relevance
    const sortedItems = mixedItems.sort((a, b) => {
        const aText = typeof a.option === 'string' ? a.option : a.option.title;
        const bText = typeof b.option === 'string' ? b.option : b.option.title;
        const queryLower = query.toLowerCase();
        const aLower = aText.toLowerCase();
        const bLower = bText.toLowerCase();

        // Exact matches first
        if (aLower === queryLower && bLower !== queryLower) return -1;
        if (bLower === queryLower && aLower !== queryLower) return 1;

        // Then starts with query
        const aStartsWith = aLower.startsWith(queryLower);
        const bStartsWith = bLower.startsWith(queryLower);
        if (aStartsWith && !bStartsWith) return -1;
        if (bStartsWith && !aStartsWith) return 1;

        // Prioritize keywords over other types
        if (a.groupType === 'keyword' && b.groupType !== 'keyword') return -1;
        if (b.groupType === 'keyword' && a.groupType !== 'keyword') return 1;

        // Keep original order for same type
        return 0;
    });

    // Limit to 10 items
    const limitedItems = sortedItems.slice(0, 10);

    return (
        <div className="my-5 space-y-2.5 md:my-2.5 md:space-y-1.5">
            {limitedItems.map((item) => {
                // Use the same unique key logic for React keys
                const itemKey =
                    typeof item.option === 'string'
                        ? `keyword:${item.option}`
                        : `${item.groupType}:${item.option.link}:${item.option.title}`;

                return (
                    <MixedSuggestionItem
                        key={itemKey}
                        option={item.option}
                        groupType={item.groupType}
                        query={query}
                        variant={variant}
                        menuColor={menuColor}
                        onClick={() => {
                            onOptionClick(item.option);
                            trackingConfig.onSuggestedCategoryClick(
                                typeof item.option === 'string'
                                    ? item.option
                                    : item.option.title,
                            );
                        }}
                    />
                );
            })}
        </div>
    );
};
